// Moving the help panel around.
//
// The panel is `position: fixed`, docked to a corner by CSS (bottom + the
// data-side edge). Dragging it switches it to explicit left/top coordinates —
// which is why every value here is a viewport coordinate, not an offset from
// whichever edge the CSS happened to use.
//
// Readers drag the panel because it is covering the thing they need help with,
// so the one rule that matters is that it can never be dragged out of reach:
// clamp() keeps it inside the viewport on every axis, and is re-applied on
// resize (a smaller window must not strand a panel off-screen).

export type Position = { left: number; top: number };

/** Gap kept between the panel and the viewport edge — matches the CSS dock. */
export const DRAG_MARGIN = 8;

export type Point = { x: number; y: number };

/**
 * How far the pointer has to travel before a press on the header is a drag.
 *
 * The header is also a click target — on a collapsed panel the whole bar
 * restores it — so a press that barely moves has to stay a click: it must not
 * take the panel off its CSS dock (a collapsed bar would stay pinned wherever
 * it was clicked instead of returning to the bottom), and a press that did move
 * must not ALSO count as the click that restores the bar.
 */
export const DRAG_THRESHOLD = 4;

/** Whether a press that started at `from` has travelled far enough to be a drag. */
export function pastDragThreshold(from: Point, to: Point): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) >= DRAG_THRESHOLD;
}

export type Viewport = { width: number; height: number };

/**
 * Nearest in-viewport position for a panel of `size` placed at `pos`.
 *
 * When the panel is larger than the viewport on an axis (a tall panel in a short
 * window) the max clamp goes negative and would win over the min, pinning the
 * panel bottom-up. Math.max(margin, …) on the upper bound keeps the top-left
 * corner visible instead, which is the edge the header — and therefore the drag
 * handle and the close button — lives on.
 */
export function clamp(pos: Position, size: Viewport, viewport: Viewport): Position {
  const maxLeft = Math.max(DRAG_MARGIN, viewport.width - size.width - DRAG_MARGIN);
  const maxTop = Math.max(DRAG_MARGIN, viewport.height - size.height - DRAG_MARGIN);
  return {
    left: Math.min(Math.max(pos.left, DRAG_MARGIN), maxLeft),
    top: Math.min(Math.max(pos.top, DRAG_MARGIN), maxTop),
  };
}

/**
 * Whether a pointerdown inside the header should start a drag.
 *
 * The header is also a toolbar: it holds back/expand/collapse/close and the
 * article's copy-link button. A press that lands on any of them (or on the SVG
 * inside one) is a click, not a grab — otherwise the buttons become unusable,
 * since a 2px twitch during the click would turn into a drag.
 *
 * Only the primary button drags: a middle-click autoscroll or a right-click
 * context menu must not move the panel.
 */
export function isDragHandle(target: EventTarget | null, button: number): boolean {
  if (button !== 0) return false;
  const el = target as Element | null;
  if (!el || typeof el.closest !== "function") return false;
  return !el.closest("button, a, input, textarea, select");
}
